<script>
import {
  defineAsyncComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';
import { isUpgradePageBypassRoute } from 'dashboard/helper/routeHelpers';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  useBuzzerBridgeSip,
  subscribeToBuzzerBridgeCallStatus,
} from 'dashboard/composables/useBuzzerBridgeSip';
import {
  BUZZER_BRIDGE_CALL_STATUS,
  BUZZER_BRIDGE_REGISTRATION_STATUS,
} from 'dashboard/services/BuzzerBridgeSipClient';
import BuzzerBridgeCallsAPI from 'dashboard/api/channel/voice/buzzerBridgeCallsAPI';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const store = useStore();
    const currentUser = useMapGetter('getCurrentUser');
    const bridgeSession = useBuzzerBridgeSip();
    let unsubscribeFromBridgeCallStatus;

    let lastBridgeCall = null;
    const bridgeCall = () =>
      store.getters['calls/getCalls'].find(
        call => call.provider === VOICE_CALL_PROVIDERS.BRIDGE
      ) || lastBridgeCall;

    const reportBridgeCall = (call, payload) => {
      if (!call?.callId) return;
      BuzzerBridgeCallsAPI.report(call.callId, payload).catch(error => {
        // eslint-disable-next-line no-console
        console.error('Failed to report Buzzer Bridge call status', error);
      });
    };

    const readyCalls = new Set();
    watch(
      () => [
        bridgeSession.registrationStatus.value,
        store.getters['calls/getCalls'],
      ],
      async ([registrationStatus, calls]) => {
        lastBridgeCall =
          calls.find(item => item.provider === VOICE_CALL_PROVIDERS.BRIDGE) ||
          lastBridgeCall;
        if (registrationStatus !== BUZZER_BRIDGE_REGISTRATION_STATUS.REGISTERED)
          return;
        const call = calls.find(
          item =>
            item.provider === VOICE_CALL_PROVIDERS.BRIDGE &&
            item.callDirection === 'inbound'
        );
        if (!call || readyCalls.has(call.callId)) return;
        readyCalls.add(call.callId);
        try {
          await BuzzerBridgeCallsAPI.ready(call.callId);
        } catch (error) {
          // The reservation stays server-side; the stale-reservation sweep releases it.
          store.dispatch('calls/dismissCall', call.callSid);
          // eslint-disable-next-line no-console
          console.error('Failed to connect incoming Buzzer Bridge call', error);
        }
      },
      { deep: true, flush: 'sync' }
    );

    watch(
      () => currentUser.value?.agent_voicecalls_bridge_credentials,
      credentials => {
        if (credentials) {
          bridgeSession
            .register(credentials)
            .then(async () => {
              const calls = await BuzzerBridgeCallsAPI.pending();
              calls
                .filter(
                  call =>
                    call.direction === 'inbound' && call.status === 'ringing'
                )
                .forEach(call => {
                  store.dispatch('calls/addCall', {
                    callSid: call.call_sid,
                    callId: call.id,
                    conversationId: call.conversation_id,
                    inboxId: call.inbox_id,
                    callDirection: call.direction,
                    caller: call.caller,
                    provider: VOICE_CALL_PROVIDERS.BRIDGE,
                  });
                });
            })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.error('Buzzer Bridge SIP registration failed', error);
            });
        } else if (
          bridgeSession.registrationStatus.value !==
          BUZZER_BRIDGE_REGISTRATION_STATUS.UNREGISTERED
        ) {
          bridgeSession.unregister();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      unsubscribeFromBridgeCallStatus = subscribeToBuzzerBridgeCallStatus(
        ({ status, reason }) => {
          const call = bridgeCall();
          if (!call) return;

          if (status === BUZZER_BRIDGE_CALL_STATUS.CONNECTED) {
            store.dispatch('calls/setCallActive', call.callSid);
            reportBridgeCall(call, { status: 'in_progress' });
          } else if (status === BUZZER_BRIDGE_CALL_STATUS.IDLE && reason) {
            // An inbound ring that ended without connecting (the Bridge gave up
            // ringing, or the agent declined) is not the end of the call: the
            // caller is still queued and the agent-ready job hands them to the
            // next agent. Only a call this browser was actually on is reported.
            const unansweredInbound =
              call.callDirection === 'inbound' && !call.isActive;
            if (!unansweredInbound) {
              reportBridgeCall(call, {
                status: ['local', 'remote'].includes(reason)
                  ? 'completed'
                  : 'failed',
                endReason: reason,
              });
            }
            store.dispatch('calls/dismissCall', call.callSid);
            lastBridgeCall = null;
          }
        }
      );
    });

    onBeforeUnmount(() => {
      unsubscribeFromBridgeCallStatus?.();
      bridgeSession.unregister();
    });

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => store.getters['calls/hasActiveCall']),
      hasIncomingCall: computed(() => store.getters['calls/hasIncomingCall']),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    isAccountPaywalled() {
      return this.upgradePageRef?.isAccountPaywalled;
    },
    bypassUpgradePage() {
      return isUpgradePageBypassRoute(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <router-view />
        <CopilotLauncher />
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <CopilotContainer />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <CommandBar :is-paywalled="isAccountPaywalled" />
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>
